<template>
  <div class="sw-modal confirm-dialog">
    <div v-click-outside="cancelAction" class="sw-modal-content" @click.stop>
      <div class="row sw-modal-heading">
        <div class="col">
          <h4>{{ singleSelection ? 'Attach a File' : 'Attach Files' }}</h4>
        </div>
      </div>
      <div class="sw-modal-body">
        <div class="row">
            <div class="col-12">
                <span v-if="singleSelection"><h5>Select one file</h5></span>
                <span v-else-if="multipleSelection"><h5>Select multiple files</h5></span>
                <span v-else class="ml-1"><h5>Select one un-tagged file or multiple tagged files</h5></span>
                <div v-if="error" class="col-12 alert alert-danger" role="alert">
                    {{error}}
                </div>
            </div>
        </div>

        <div class="row">
          <div class="col-12">
            <template v-if="hasFiles">
              <div v-for="(file, index) in getFiles"
                   v-bind:key="index"
                   class="edit-establishment__images-thumbnail edit-establishment__images-thumbnail--selectable edit-establishment__images-thumbnail--file"
                   v-on:click.stop="select(file.reference)"
              >
                <span v-if="file.roomType" class="edit-establishment__images-thumbnail__tag edit-establishment__images-thumbnail__tag--set">
                    <i class="fas fa-tag"></i>
                </span>
                <img v-if="isImage(file)" v-bind:src="file.url"/>
                <div v-else class="edit-establishment__images-thumbnail__doc">
                  <i class="fas fa-file-image"></i>
                </div>
                <div v-if="!isImage(file)" class="edit-establishment__images-thumbnail-filename">
                  <div>{{file.filename}}</div>
                </div>
                <div v-if="file.selected" class="file-selector__file--selected">
                  <i class="fas fa-paperclip"></i>
                </div>
              </div>
            </template>
            <template v-else>
              <p class="pl-4">No uploaded files to choose from</p>
            </template>
          </div>
        </div>
        <div class="row mb-3">
            <div class="col-12 question__question text-center">
                OR
            </div>
        </div>
        <div class="row">
            <div class="col-12 question__question file-selector__upload">
                <h5>Upload a file</h5>
                <div @click="showRules = !showRules">
                    see rules
                    <i v-if="showRules" class="fas fa-chevron-up"></i>
                    <i v-if="!showRules" class="fas fa-chevron-down"></i>
                </div>
            </div>
            <div class="col-12 text-note" v-show="showRules">
                <!-- Please upload high resolution -->
                <ul class="mb-0">
                    <li><b>images</b>: JPG, GIF, or PNG (up to 20MB)</li>
                    <!-- <li><b>videos</b>: MP4, AVI or MOV (up to 40MB)</li> -->
                    <li><b>videos</b>: MP4, AVI or MOV (up to 16MB)</li>
                    <li><b>documents</b>: PDF etc (up to 16MB)</li>
                </ul>
            </div>
            <div class="col-12">
                <div class="edit-establishment__images-dropbox">
                    <form enctype="multipart/form-data" novalidate v-if="isInitial || isSaving">
                    <input type="file" multiple
                            v-bind:name="uploadFieldName"
                            v-bind:disabled="isSaving"
                            v-on:change="filesChange($event.target.name, $event.target.files); fileCount = $event.target.files.length"
                            v-bind:accept="acceptedFileTypes"
                            class="edit-establishment__images-input-file">
                    <p v-if="isInitial">
                        Drag your file(s) here to upload<br> or click to browse
                    </p>
                    <p v-if="isSaving">
                        Uploading {{ fileCount }} files...
                    </p>
                    </form>
                    <div class="row edit-establishment__images-feedback" v-if="isSuccess || isFailed">
                        <!--SUCCESS-->
                        <div v-if="isSuccess" class="col-12 alert alert-success" role="alert">
                            Uploaded {{ fileCount }} file(s) successfully
                        </div>
                        <!--FAILED-->
                        <div v-if="isFailed" class="col-12 alert alert-danger" role="alert">
                            Uploaded failed
                            <pre>{{ uploadError }}</pre>
                            <a href="javascript:void(0)" @click.stop="reset()">Try again</a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="sw-modal-actions">
          <div class="row">
            <div class="col text-center">
              <button class="btn btn-normal" v-on:click.stop="cancelAction()">Cancel</button>
              <button v-if="hasFiles" v-bind:disabled="error || !isFileSelected" class="btn btn-primary" v-on:click.stop="selectAction()">Select</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
    import Vue from 'vue';

    import { filesMixin } from '../../mixins/FilesMixin.js';

    export const SELECTION_MODES = {
        MULTIPLE: 0,
        SINGLE: 1,
        SINGLE_OR_TAGGED: 2
    };

    export const SELECTION_MODE_LABELS = {
        [SELECTION_MODES.MULTIPLE]: 'Select Files',
        [SELECTION_MODES.SINGLE]: 'Select File', 
        [SELECTION_MODES.SINGLE_OR_TAGGED]: 'Select File or Tagged Files'
    };

    export default {
        name: 'FileSelectorDialog',
        components: {
        },
        mixins: [filesMixin],
        props: {
            selectedFiles: {
                type: Array,
                default: () => []
            },
            selectionMode: {
                type: Number,
                default: SELECTION_MODES.MULTIPLE,
                validator: (value) => Object.values(SELECTION_MODES).includes(value)
            },
            selectionRequired: {
                type: Boolean,
                default: false
            }
        },
        data: function() {
            return {
                showRules: false,
                error: null
            }
        },
        computed: {
            hasFiles: function() {
                return this.establishment.preferences.files.length > 0;
            },
            singleSelection: function() {
                return this.selectionMode === SELECTION_MODES.SINGLE;
            },
            multipleSelection: function() {
                return this.selectionMode === SELECTION_MODES.MULTIPLE;
            },
            isFileSelected: function() {
                return this.getFiles.some(file => file.selected);
            }
        },
        methods: {
            setupInitialSelections: function() {
                if (!this.selectedFiles || this.selectedFiles.length === 0) return;

                for (let i = 0; i < this.getFiles.length; i++) {
                    let file = this.getFiles[i];
                    if (this.selectedFiles.some(selected => selected.reference === file.reference)) {
                        Vue.set(file, 'selected', true);
                    }
                }
            },
            checkIfSelectionRequiredButNothingSelected: function() {
                if (this.selectionRequired) {
                    const selectedFiles = this.getFiles.filter(file => file.selected);
                    if (selectedFiles.length === 0) {
                        this.error = 'Please select at least one file.';
                        return true;
                    } else {
                        this.error = null;
                        return false;
                    }
                }
                return false;
            },
            select: function(reference) {
                let file = this.getFiles.find(f => f.reference === reference);
                console.log('Selected file:', file);
                if (!file) return;

                if (file.selected) {
                    Vue.set(file, 'selected', false);
                    this.checkIfSelectionRequiredButNothingSelected();
                    return;
                }

                let selectedFiles = this.getFiles.filter(f => f.selected);
                console.log('Currently selected files:', selectedFiles);

                // Handle different selection modes
                switch (this.selectionMode) {
                    case SELECTION_MODES.SINGLE:
                        // Clear all selections first
                        this.clearAllSelections();
                        Vue.set(file, 'selected', true);
                        console.log('After selection (1), selected files:', this.getFiles.filter(f => f.selected));
                        break;

                    case SELECTION_MODES.SINGLE_OR_TAGGED:
                        
                        if (file.roomType) {
                            // Selecting a tagged file - allow if no non-tagged files selected
                            const hasNonTaggedSelection = selectedFiles.some(f => !f.roomType);
                            if (hasNonTaggedSelection) {
                                this.clearAllSelections();
                            }
                            Vue.set(file, 'selected', true);
                        } else {
                            // Selecting a non-tagged file - clear all selections first
                            this.clearAllSelections();
                            Vue.set(file, 'selected', true);
                        }
                        console.log('After selection (2), selected files:', this.getFiles.filter(f => f.selected));
                        break;

                    case SELECTION_MODES.MULTIPLE:
                    default:
                        Vue.set(file, 'selected', true);
                        console.log('After selection (3), selected files:', this.getFiles.filter(f => f.selected));
                        break;
                }

                this.error = null; // Clear any previous error
            },
            clearAllSelections: function() {
                for (let i = 0; i < this.getFiles.length; i++) {
                    Vue.set(this.getFiles[i], 'selected', false);
                }
            },
            cancelAction: function() {
                this.clearAllSelections();
                this.$root.$emit("finished-selecting-files");
            },
            selectAction: function() {
                const selectedFiles = this.getFiles.filter(file => file.selected);
                if (this.checkIfSelectionRequiredButNothingSelected()) {
                    return;
                }
                this.clearAllSelections();
                this.$root.$emit("finished-selecting-files", selectedFiles);
            },
            filesUploaded: function(uploadedFiles) {
                this.clearAllSelections();
                var filesToSelect = uploadedFiles.filter(file => this.getFiles.some(f => f.reference === file.reference));
                if (filesToSelect.length === 0) {
                    return;
                }
                if (this.singleSelection && filesToSelect.length > 0) {
                    Vue.set(filesToSelect[0], 'selected', true);
                } else {
                    for (let i = 0; i < filesToSelect.length; i++) {
                        Vue.set(filesToSelect[i], 'selected', true);
                    }
                }
            }
        },
        mounted() {
            this.reset();
            this.clearAllSelections();
            this.setupInitialSelections();

            this.$root.$on("uploaded-files", this.filesUploaded);
        },
        beforeDestroy() {
            this.$root.$off("uploaded-files", this.filesUploaded);
        },
    }
</script>

<style lang="scss" scoped>
  @import "../../styles/components/_modal.scss";
  @import "../../styles/components/_edit_establishment";
  @import "../../styles/components/_file_selector_dialog.scss";
</style>